import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { brands } from "../config/config";
import { apiUrl } from "../config/config";

const InstantQuoteWidget = () => {
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    patch_type: "",
    backing_type: "",
    quantity: "",
    width: "",
    height: "",
    comments: ""
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});

  const modalRef = useRef(null);
  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value
    });

    setErrors({
      ...errors,
      [name]: ""
    });
  };

  // close on route change
  useEffect(() => {
    setOpen(false);
  }, [location]);

  // outside click close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // ---------- Validation ----------
  const validate = () => {
    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name required";

    if (!form.email) {
      newErrors.email = "Email required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "Invalid email";
    }

    if (!form.phone) newErrors.phone = "Phone required";

    if (!form.quantity) newErrors.quantity = "Quantity required";

    if (!form.width) newErrors.width = "Width required";

    if (!form.height) newErrors.height = "Height required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    if (!validate()) return;

    const formData = new FormData();

    formData.append("name", form.name);
    formData.append("email", form.email);
    formData.append("phone", form.phone);
    formData.append("patch_type", form.patch_type);
    formData.append("backing_type", form.backing_type);

    formData.append("quantity", parseInt(form.quantity));
    formData.append("width", parseFloat(form.width));
    formData.append("height", parseFloat(form.height));

    formData.append("comments", form.comments);

    // send brand
    formData.append("brand", brands);

    if (file) formData.append("image", file);

    try {
      const response = await fetch(`${apiUrl}/api/instant-quote`, {
        method: "POST",
        headers: {
          Accept: "application/json"
        },
        body: formData
      });

      const data = await response.json();

      // backend validation errors
      if (data.errors) {
        setErrors(data.errors);
        return;
      }

      if (data.status) {
        alert("Quote Submitted!");

        setForm({
          name: "",
          email: "",
          phone: "",
          patch_type: "",
          backing_type: "",
          quantity: "",
          width: "",
          height: "",
          comments: ""
        });

        setFile(null);
        setOpen(false);
      }
    } catch (err) {
      alert("Server Error");
    }
  };

  return (
    <>
      {/* Floating Button */}
      <div
        onClick={() => setOpen((prev) => !prev)}
        className="fixed bottom-6 left-6 z-[9999] flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-[#1e242a] text-2xl text-white shadow-[0_18px_45px_rgba(30,36,42,0.28)] transition-all duration-300 hover:-translate-y-1 hover:bg-[#37404a]"
      >
        💬
      </div>

      {open && (
        <div
          ref={modalRef}
          className="fixed bottom-24 left-6 z-[9999] w-[90vw] overflow-hidden rounded-2xl border border-[#1e242a]/10 bg-white shadow-2xl sm:w-[360px]"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#1e242a] px-4 py-3 text-white">
            <span className="font-semibold">Get Instant Quote</span>

            <button
              onClick={() => setOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-300 hover:bg-white/20"
            >
              ✕
            </button>
          </div>

          {/* Form */}
          <div className="max-h-[420px] space-y-2 overflow-y-auto bg-[#f5f6f5] p-4">
            <InputField
              name="name"
              placeholder="Name"
              value={form.name}
              onChange={handleChange}
              error={errors.name}
            />

            <InputField
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              error={errors.email}
            />

            <InputField
              name="phone"
              type="number"
              placeholder="Phone"
              value={form.phone}
              onChange={handleChange}
              error={errors.phone}
            />

            {/* Patch Type Dropdown */}
            <div>
              <select
                name="patch_type"
                value={form.patch_type}
                onChange={handleChange}
                className={inputStyle}
              >
                <option value="">Select Patch Type</option>
                <option value="Standard Embroidery">Standard Embroidery</option>
                <option value="3D Embroidery">3D Embroidery</option>
                <option value="PVC Rubber">PVC Rubber</option>
                <option value="Leather">Leather</option>
                <option value="Chenille">Chenille</option>
                <option value="Woven">Woven</option>
                <option value="Printed">Printed</option>
                <option value="Bullion">Bullion</option>
                <option value="Key Chain">Key Chain</option>
              </select>
            </div>

            {/* Backing Type Dropdown */}
            <div>
              <select
                name="backing_type"
                value={form.backing_type}
                onChange={handleChange}
                className={inputStyle}
              >
                <option value="">Select Backing Type</option>
                <option value="Iron-On">Iron-On</option>
                <option value="Velcro">Velcro</option>
                <option value="Plastic">Plastic</option>
                <option value="Tape/Adhesive">Tape/Adhesive</option>
                <option value="Sew-On">Sew-On</option>
              </select>
            </div>

            <InputField
              type="number"
              name="quantity"
              placeholder="Quantity"
              value={form.quantity}
              onChange={handleChange}
              error={errors.quantity}
            />

            <div className="flex gap-2">
              <InputField
                type="number"
                name="width"
                placeholder="Width"
                value={form.width}
                onChange={handleChange}
                error={errors.width}
              />

              <InputField
                type="number"
                name="height"
                placeholder="Height"
                value={form.height}
                onChange={handleChange}
                error={errors.height}
              />
            </div>

            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className={inputStyle}
            />

            <textarea
              name="comments"
              placeholder="Comments"
              value={form.comments}
              onChange={handleChange}
              className={`${inputStyle} h-20 resize-none`}
            />

            <button
              onClick={submitForm}
              className="w-full rounded-md bg-[#1e242a] py-2 font-semibold text-white transition-all duration-300 hover:bg-[#37404a]"
            >
              Submit Quote
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const InputField = ({ type = "text", name, placeholder, value, onChange, error }) => (
  <div className="w-full">
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`${inputStyle} ${error ? "border-red-500 focus:ring-red-500" : ""}`}
    />

    {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
  </div>
);

const inputStyle =
  "w-full rounded-md border border-[#1e242a]/15 bg-white px-3 py-2 text-sm text-[#1e242a] placeholder:text-[#5f6870] focus:outline-none focus:ring-2 focus:ring-[#37404a]";

export default InstantQuoteWidget;